<template>
    <ion-page>
        <ion-header>
            <ion-toolbar>
                <ion-buttons slot="end">
                    <ion-button @click="close()">
                        <ion-icon slot="icon-only" :icon="closeOutline"></ion-icon>
                    </ion-button>
                </ion-buttons>
                <ion-title>{{ line.item.item_number }} / {{ line.sku.sku }}</ion-title>
            </ion-toolbar>
        </ion-header>
        <ion-content :fullscreen="true">
            <ion-list>
                <ion-item>
                    <ion-label class="ion-text-wrap">
                        <h2>Item Number</h2>
                        <p>
                            {{ line.item.item_number }} / {{ line.sku.sku }}
                            <ion-badge v-show="line.pick_b_stock" color="danger">B-Stock</ion-badge>
                            <ion-icon v-show="itemMatched" color="success" :icon="checkmarkCircle"/>
                        </p>
                    </ion-label>
                    <ion-button size="medium" color="primary" @click="openItemScanner()">
                        <ion-icon slot="icon-only" :icon="barcode"/>
                    </ion-button>
                    <ion-button expand="block" color="primary" size="medium" @click="openItemPicker()">
                        <ion-icon slot="icon-only" :icon="search"/>
                    </ion-button>
                </ion-item>
                <ion-item>
                    <ion-label class="ion-text-wrap">
                        <h2>Location</h2>
                        <p>
                            <span v-if="!locationMatched && locationViable">
                                {{ this.scannedLocation.code }}
                                (Changed)
                            </span>
                            <span v-else>
                                {{ line.location.code }}
                                <ion-icon v-show="locationMatched" color="success" :icon="checkmarkCircle"/>
                            </span>
                        </p>
                    </ion-label>
                    <ion-button size="medium" color="primary"  @click="openLocationScanner()">
                        <ion-icon slot="icon-only" :icon="barcode" />
                    </ion-button>
                    <ion-button size="medium" color="primary" @click="selectLocation()">
                        <ion-icon slot="icon-only" :icon="list"/>
                    </ion-button>
                </ion-item>
                <ion-item lines="none">
                    <ion-label class="ion-text-wrap">
                        <h2>Picking Instructions</h2>
                        <p>
                            {{ line.picking_instructions }}
                        </p>
                        <text-text color="warning" v-if="likelySubstitute">{{ substituteInformation }}</text-text>
                    </ion-label>
                </ion-item>
                <ion-item lines="none">
                    <ion-button style="width:100%" size="medium" expand="block" fill="outline" color="dark"
                                @click="$refs.product.open()">Product Card
                    </ion-button>
                </ion-item>
                <ion-item lines="none" class="ion-margin-top text">
                    <ion-label position="floating">Quantity Required</ion-label>
                    <ion-input :readonly="true" type="number" class="ion-text-center" size="large"
                               :value="line.qty_required"/>
                </ion-item>
                <ion-item>
                    <ion-label position="floating">Picking Summary</ion-label>
                    <ion-input :readonly="true" class="ion-text-center" :value="qty_description"/>
                </ion-item>
                <ion-item lines="none">
                    <big-stepper label="Quantity Picked" v-model="qty_picked"/>
                </ion-item>
            </ion-list>
            <failed-pick-count-reason-alert
                ref="alert"
                header="Pick count does not match"
                subtitle="Scroll for reasons"
                group="PICK_ERRORS"
                @submitted="save($event, $event.comment)"
            />
            <product-card-modal ref="product" :item="line.item" :sku="line.sku" :shipment-line="line"/>
        </ion-content>
        <ion-footer>
            <ion-button size="large" color="secondary" expand="full" @click="submit()">Pick</ion-button>
        </ion-footer>
    </ion-page>
</template>

<script>
import {modalController, pickerController, alertController} from '@ionic/vue';
import {
    IonContent, IonPage, IonLabel, IonItem, IonHeader, IonToolbar, IonBadge,
    IonTitle, IonButtons, IonButton, IonIcon, IonInput, IonFooter, IonList,
} from '@ionic/vue';

import EventBus from "@/EventBus";
import BigStepper from "@/components/BigStepper";
import FailedPickCountReasonAlert from "@/components/Alerts/FailedPickCountReasonAlert";
import {closeOutline, list, locate, search, barcode, checkmarkCircle} from 'ionicons/icons';
import LocationSearchModalHelper from "@/services/LocationSearchModalHelper";
import ItemSearchModalHelper from "@/services/ItemSearchModalHelper";
import {Capacitor} from '@capacitor/core';
import ProductCardModal from "@/components/Modals/ProductCardModal";
import {map} from "lodash";

export default {
    props: ['line'],
    inject: ['$http', '$toast', '$validationAlert', '$scanner'],
    name: 'PickLine',
    components: {
        ProductCardModal,
        FailedPickCountReasonAlert,
        BigStepper,
        IonContent, IonPage, IonLabel, IonItem, IonHeader, IonToolbar, IonBadge,
        IonTitle, IonButtons, IonButton, IonIcon, IonInput, IonFooter, IonList
    },
    computed: {
        likelySubstitute() {
            return (this.line.sku?.item?.id !== this.line?.item?.id);
        },
        substituteInformation() {
            return `Looks like original '${this.line.sku.item.item_number}' has been substituted for '${this.line.item.item_number}'`;
        },
        params() {
            return this.$route.params;
        },
        locationViable() {
            return map(this.line.item.locations, 'code').indexOf(this.scannedLocation.code) >= 0;
        },
        locationMatched() {
            return this.scannedLocation?.code === this.line.location?.code;
        },
        itemMatched() {
            return this.scannedItem?.item_number === this.line.item?.item_number;
        },
        qty_description() {
            if (this.line.item.case_qty > 0) {
                const boxes = Math.floor(this.line.qty_required / this.line.item.case_qty);
                const singles = this.line.qty_required - (this.line.item.case_qty * boxes);
                if (singles > 0) {
                    return boxes + " boxes " + (singles > 0 ? " and " + singles + " singles" : "");
                } else {
                    return boxes + " boxes";
                }
            } else {
                return this.line.qty_required + " singles";
            }
        }
    },
    methods: {
        async selectLocation() {
            let selectedIndex = null;
            const picker = await pickerController.create({
                buttons: [{text: 'Cancel', role: 'cancel'}, {text: 'Confirm'}],
                columns: [{
                    name: 'location',
                    options: map(this.line.item.locations, (location, index) => {
                        if (!selectedIndex && this.line.location.id === location.id) {
                            selectedIndex = index;
                        }
                        return {
                            value: location,
                            text: `${location.code} R:${location.pivot.qty_reserved} A:${location.pivot.qty_total}`
                        }
                    })
                }]
            });
            picker.columns[0].selectedIndex = selectedIndex;
            await picker.present();
            picker.onDidDismiss().then(value => {
                if (value.data &&
                    value.data.location &&
                    this.scannedLocation !== value.data.location.value
                ) {
                    const newLocation = value.data.location.value;
                    let msg = `Please visit ${value.data.location.text} and scan the location barcode`;
                    // No longer setting scannedLocation just because they pick
                    // it from a list, they need to go and visit. EXCEPT: unknown and temp.
                    if (newLocation.is_temporary || newLocation.is_unknown) {
                        this.scannedLocation = newLocation;
                        msg = 'Unknown or Temporary location selected - proceed with pick';
                    }

                    alertController.create({
                        message: msg,
                        buttons: ['Ok']
                    }).then(a => {
                        a.present();
                    })

                }
            });
        },
        openLocationScanner() {
            if (Capacitor.platform === 'web') {
                this.scannedLocation = this.line.location;
                return;
            }
            this.$scanner.scan('location').then(result => {
                this.scannedLocation = result.extra;
            });
        },
        openItemScanner() {
            if (Capacitor.platform === 'web') {
                this.scannedItem = this.line.item;
                return;
            }
            this.$scanner.search().then(result => {
                this.scannedItem = result;
            });
        },
        openItemPicker() {
            ItemSearchModalHelper.open().then(item => {
                this.scannedItem = item.data;
            });
        },
        openLocationPicker() {
            LocationSearchModalHelper.open().then(location => {
                this.scannedLocation = location.data;
            });
        },
        close() {
            modalController.dismiss();
        },
        /**
         * Update the shipment line. By the time we call this
         * we've already sought the reason and comment if
         * required. They'll be passed in ...
         *
         * @param reason
         * @param comment
         */
        save(reason = {}, comment = '') {
            EventBus().emitter.emit('loading', true);
            // send through a payload which contains: picked (count),
            // reason (object with id as a key), comment (string)
            this.$http.put(`/api/picks/me/lines/${this.line.id}`, {
                item: this.scannedItem,
                location: this.scannedLocation,
                picked: this.qty_picked,
                reason: reason.reason,
                comment: comment
            }).then(() => {
                EventBus().emitter.emit('loading', false);
                // not using finally here to dismiss loading. Instead,
                // we're going to use a silly setTimeout hack ...
                // There's an event bound to when the modal is dismissed
                // which, in turn, triggers a loading event. To prevent
                // crossed wires, and overlapping loading events, we
                // try to separate out the dismiss call from the loading:false
                setTimeout(() => {
                    modalController.dismiss(true);
                }, 500);
            }).catch(error => {
                EventBus().emitter.emit('loading', false);
                this.$validationAlert(error);
            });
        },
        /**
         * If required, this will present the "choose an anomoly
         * reason".
         */
        submit() {
            // when we used !== it'd say that 24 != 24 or something
            // stupid, so we're doing a looser check...
            if (this.qty_picked != this.line.qty_required) {
                this.$refs['alert'].present();
            } else {
                this.save();
            }
        }
    },
    data() {
        return {
            scannedLocation: {},
            scannedItem: {},
            qty_picked: this.line.qty_picked,
        }
    },
    setup() {
        return {
            checkmarkCircle,
            barcode,
            search,
            list,
            locate,
            closeOutline
        };
    }
}
</script>
<style scoped>
ion-input {
    font-size: 200%;
}
</style>

